import React, { useMemo, useCallback, useState } from 'react';
import { Panel } from 'rsuite';
import { isMobile } from 'react-device-detect';
import { useParams, Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesSidebarNav from './RulesSidebarNav';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { RULES_SECTIONS } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  sectionTitleMarginBottom: 8,
  sectionDescriptionMarginBottom: 24,
  panelMarginBottom: 16,
  sidebarWidth: 240,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarItemPadding: '6px 0',
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  stickyTop: 20,
  navFooterMarginTop: 40,
  navFooterPadding: '20px 0',
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  defaultExpandedCount: 3,
  notFoundPadding: 60,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  description: 15,
  descriptionMobile: 14,
  sidebarTitle: 13,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  subsectionTitle: 18,
  subsectionContent: 14,
  subsectionLineHeight: 1.7,
  navLink: 14,
  notFoundTitle: 24,
  notFoundMessage: 15,
  backLink: 14,
};

const RulesSection = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const { sectionId } = useParams();
  const [activeId, setActiveId] = useState('');

  const sectionIndex = useMemo(
    () => RULES_SECTIONS.findIndex((s) => s.id === sectionId),
    [sectionId]
  );

  const section = sectionIndex >= 0 ? RULES_SECTIONS[sectionIndex] : null;
  const prevSection = sectionIndex > 0 ? RULES_SECTIONS[sectionIndex - 1] : null;
  const nextSection =
    sectionIndex >= 0 && sectionIndex < RULES_SECTIONS.length - 1
      ? RULES_SECTIONS[sectionIndex + 1]
      : null;

  useScrollToTop(sectionId);

  const scrollToSubsection = useCallback((subsectionId) => {
    setActiveId(subsectionId);
    const el = document.getElementById(subsectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  if (!section) {
    return (
      <>
        <NavigationBar />
        <div
          style={{
            maxWidth: SPACING.containerMaxWidth,
            margin: '0 auto',
            padding: isMobile
              ? SPACING.containerPaddingMobile
              : SPACING.containerPadding,
            textAlign: 'center',
            paddingTop: SPACING.notFoundPadding,
          }}
        >
          <h2 style={{ color: t.textPrimary, fontSize: FONT.notFoundTitle }}>
            Section Not Found
          </h2>
          <p style={{ color: t.textMuted, fontSize: FONT.notFoundMessage }}>
            The rules section "{sectionId}" does not exist.
          </p>
          <Link
            to="/rules"
            style={{ color: t.accentPrimary, fontSize: FONT.backLink }}
          >
            ← Back to Rules Reference
          </Link>
        </div>
      </>
    );
  }

  const subsections = section.subsections || [];
  const sidebarItems = subsections.map((sub) => ({ id: sub.id, label: sub.title }));

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile
            ? SPACING.containerPaddingMobile
            : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.backLinkMarginBottom }}>
          <Link
            to="/rules"
            style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}
          >
            ← Back to Rules Reference
          </Link>
        </div>

        {/* Section header */}
        <div style={{ marginBottom: SPACING.sectionDescriptionMarginBottom }}>
          <h1
            style={{
              fontSize: isMobile ? FONT.titleMobile : FONT.title,
              color: t.textPrimary,
              marginBottom: SPACING.sectionTitleMarginBottom,
            }}
          >
            {section.icon} {section.title}
          </h1>
          <p
            style={{
              fontSize: isMobile ? FONT.descriptionMobile : FONT.description,
              color: t.textSecondary,
              margin: 0,
            }}
          >
            {section.description}
          </p>
        </div>

        {/* Layout: sidebar + content */}
        {subsections.length > 0 && (
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={sidebarItems}
            activeId={activeId}
            onSelect={scrollToSubsection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Subsection panels */}
            {subsections.map((sub, idx) => (
              <div
                key={sub.id}
                id={sub.id}
                style={{ marginBottom: SPACING.panelMarginBottom }}
              >
                <Panel
                  header={
                    <span style={{ fontSize: FONT.subsectionTitle, color: t.textPrimary }}>
                      {sub.title}
                    </span>
                  }
                  collapsible
                  defaultExpanded={idx < SPACING.defaultExpandedCount}
                  bordered
                  style={{
                    backgroundColor: t.cardBackground,
                    borderColor: t.borderPrimary,
                  }}
                >
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: FONT.subsectionContent,
                      color: t.textSecondary,
                      lineHeight: FONT.subsectionLineHeight,
                    }}
                  >
                    {sub.content}
                  </div>
                </Panel>
              </div>
            ))}

            {/* Previous / Next navigation */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: SPACING.navFooterMarginTop,
                paddingTop: SPACING.navFooterPadding,
                borderTop: `1px solid ${t.borderPrimary}`,
              }}
            >
              <div>
                {prevSection && (
                  <Link
                    to={`/rules/${prevSection.id}`}
                    style={{
                      color: t.accentPrimary,
                      fontSize: FONT.navLink,
                      textDecoration: 'none',
                    }}
                  >
                    ← {prevSection.title}
                  </Link>
                )}
              </div>
              <div>
                {nextSection && (
                  <Link
                    to={`/rules/${nextSection.id}`}
                    style={{
                      color: t.accentPrimary,
                      fontSize: FONT.navLink,
                      textDecoration: 'none',
                    }}
                  >
                    {nextSection.title} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        )}
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default RulesSection;
